import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDown, Info, SlidersHorizontal, Eye } from 'lucide-react';
import LauncherThumbnail from './LauncherThumbnail';
import ProfileSelectorModal from './ProfileSelectorModal';
import SplitRunButton from './SplitRunButton';
import AdvancedRunModal from './AdvancedRunModal';
import EnvPreviewModal from '../../../Admin/LauncherDashboard/components/EnvPreviewModal';

/**
 * LauncherCard - Displays a single launcher with profile selector
 * 
 * Props:
 * - launcher: LauncherData object
 * - machineCapabilities: Array of machine capabilities for filtering profiles
 * - machine: MachineData object (for advanced run modal display)
 * - onLaunch: (launcher, profile, options?) => Promise<void> - Async callback when Run is clicked
 * - onShowDetails: (launcher, profile, capability) => void - Callback when Info is clicked
 * - isLaunching: boolean - External loading state (optional)
 */
const LauncherCard = ({ 
  launcher, 
  machineCapabilities = [], 
  machine,
  spaceId = null,
  onLaunch,
  onShowDetails,
  isLaunching: externalIsLaunching = false
}) => {
  // Filter profiles to only those available on the selected machine
  const availableProfiles = useMemo(() => {
    return launcher.getAvailableProfiles(machineCapabilities);
  }, [launcher, machineCapabilities]);

  // Track selected profile (default to first available or launcher's default)
  const [selectedProfile, setSelectedProfile] = useState(() => {
    const defaultInAvailable = availableProfiles.find(p => p.is_default);
    return defaultInAvailable || availableProfiles[0] || null;
  });

  // Update selected profile when available profiles change (machine changed)
  useEffect(() => {
    if (availableProfiles.length > 0) {
      const stillAvailable = availableProfiles.find(p => p.profile_id === selectedProfile?.profile_id);
      if (!stillAvailable) {
        const defaultInAvailable = availableProfiles.find(p => p.is_default);
        setSelectedProfile(defaultInAvailable || availableProfiles[0]);
      }
    } else {
      setSelectedProfile(null);
    }
  }, [availableProfiles, selectedProfile?.profile_id]);

  // Profile dropdown state
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  
  // Advanced profile selector modal state
  const [showProfileSelector, setShowProfileSelector] = useState(false);
  
  // Advanced run modal state
  const [showAdvancedRun, setShowAdvancedRun] = useState(false);
  
  // Env preview modal state
  const [showEnvPreview, setShowEnvPreview] = useState(false);
  
  // Internal loading state
  const [isLaunchingInternal, setIsLaunchingInternal] = useState(false);
  const isLaunching = externalIsLaunching || isLaunchingInternal;

  const handleLaunch = async (options = {}) => {
    if (!selectedProfile || !onLaunch || isLaunching) return;
    
    setIsLaunchingInternal(true);
    try {
      await onLaunch(launcher, selectedProfile, options);
    } finally {
      setIsLaunchingInternal(false);
    }
  };

  const handleAdvancedLaunch = async ({ flags, envOverrides }) => {
    if (!selectedProfile || !onLaunch || isLaunching) return;
    
    // Parse flags string into array (split on whitespace, but handle quotes in future)
    const additionalArgs = flags ? flags.split(/\s+/).filter(Boolean) : [];
    
    setIsLaunchingInternal(true);
    try {
      await onLaunch(launcher, selectedProfile, {
        additionalArgs,
        environmentOverrides: envOverrides
      });
      setShowAdvancedRun(false);
    } finally {
      setIsLaunchingInternal(false);
    }
  };

  const handleShowDetails = () => {
    if (onShowDetails && selectedProfile) {
      // Get the capability for this profile on the current machine
      const capability = machineCapabilities.find(c => c.profile_id === selectedProfile.profile_id);
      onShowDetails(launcher, selectedProfile, capability);
    }
  };

  const handleProfileSelect = (profile) => {
    setSelectedProfile(profile);
  };

  return (
    <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg overflow-hidden hover:border-gray-600/50 transition-colors group">
      {/* Card content */}
      <div className="p-4 flex gap-4">
        {/* Thumbnail - w-40 h-40 to match AppCard desktop size */}
        <div className="flex-shrink-0 w-40 h-40 rounded-lg overflow-hidden">
          <LauncherThumbnail 
            launcherId={launcher.launcher_id}
            iconUrl={launcher.icon_url}
            className="w-full h-full"
          />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-medium text-white truncate group-hover:text-blue-400 transition-colors">
                {launcher.display_name}
              </h3>
              <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-gray-700 text-gray-400">
                {launcher.category}
              </span>
            </div>
            
            {/* Info button - shows profile details */}
            <button
              onClick={handleShowDetails}
              disabled={!selectedProfile}
              className={`
                p-1.5 rounded transition-colors
                ${selectedProfile 
                  ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-700/50' 
                  : 'text-gray-600 cursor-not-allowed'
                }
              `}
              title={selectedProfile ? `View ${selectedProfile.name} details` : 'No profile selected'}
            >
              <Info size={16} />
            </button>
          </div>

          {launcher.description && (
            <p className="mt-2 text-sm text-gray-400 line-clamp-2">
              {launcher.description}
            </p>
          )}
        </div>
      </div>

      {/* Actions bar */}
      <div className="px-4 py-3 bg-gray-900/30 border-t border-gray-700/50 flex items-center gap-2">
        {/* Profile selector */}
        <div className="relative flex-1">
          <button
            onClick={() => !isLaunching && setShowProfileMenu(!showProfileMenu)}
            disabled={availableProfiles.length <= 1 || isLaunching}
            className={`
              w-full flex items-center justify-between gap-2 px-3 py-2 
              bg-gray-700/50 border border-gray-600 rounded-lg text-sm
              ${availableProfiles.length > 1 && !isLaunching
                ? 'hover:bg-gray-700 cursor-pointer' 
                : 'opacity-75 cursor-default'
              }
            `}
          >
            <span className="truncate text-gray-200">
              {selectedProfile?.name || 'No profiles available'}
            </span>
            {availableProfiles.length > 1 && !isLaunching && (
              <ChevronDown 
                size={16} 
                className={`text-gray-400 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`}
              />
            )}
          </button>

          {/* Profile dropdown menu */}
          {showProfileMenu && availableProfiles.length > 1 && (
            <div className="absolute z-10 bottom-full mb-1 w-full bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden">
              {availableProfiles.map(profile => (
                <button
                  key={profile.profile_id}
                  onClick={() => {
                    setSelectedProfile(profile);
                    setShowProfileMenu(false);
                  }}
                  className={`
                    w-full px-3 py-2 text-left text-sm transition-colors
                    ${selectedProfile?.profile_id === profile.profile_id 
                      ? 'bg-blue-500/20 text-blue-400' 
                      : 'text-gray-300 hover:bg-gray-700'
                    }
                  `}
                >
                  <span className="block truncate">{profile.name}</span>
                  {profile.version && (
                    <span className="text-xs text-gray-500">v{profile.version}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Env preview button */}
        <button
          onClick={() => setShowEnvPreview(true)}
          disabled={!selectedProfile || isLaunching}
          className={`
            p-2 rounded-lg border transition-colors
            ${selectedProfile && !isLaunching
              ? 'bg-gray-700/50 border-gray-600 text-green-400 hover:text-green-300 hover:bg-gray-700 hover:border-gray-500' 
              : 'bg-gray-800/50 border-gray-700 text-gray-600 cursor-not-allowed'
            }
          `}
          title="Preview environment variables"
        >
          <Eye size={18} />
        </button>

        {/* Advanced profile selector button */}
        <button
          onClick={() => setShowProfileSelector(true)}
          disabled={availableProfiles.length === 0 || isLaunching}
          className={`
            p-2 rounded-lg border transition-colors
            ${availableProfiles.length > 0 && !isLaunching
              ? 'bg-gray-700/50 border-gray-600 text-gray-400 hover:text-white hover:bg-gray-700 hover:border-gray-500' 
              : 'bg-gray-800/50 border-gray-700 text-gray-600 cursor-not-allowed'
            }
          `}
          title="Advanced profile selector"
        >
          <SlidersHorizontal size={18} />
        </button>

        {/* Split Run button with advanced options */}
        <SplitRunButton
          onRun={() => handleLaunch()}
          onAdvancedRun={() => setShowAdvancedRun(true)}
          disabled={!selectedProfile}
          isLoading={isLaunching}
        />
      </div>

      {/* Advanced Profile Selector Modal */}
      <ProfileSelectorModal
        isOpen={showProfileSelector}
        onClose={() => setShowProfileSelector(false)}
        onSelect={handleProfileSelect}
        profiles={availableProfiles}
        selectedProfile={selectedProfile}
        launcher={launcher}
        machineCapabilities={machineCapabilities}
      />

      {/* Advanced Run Modal */}
      <AdvancedRunModal
        isOpen={showAdvancedRun}
        onClose={() => setShowAdvancedRun(false)}
        onLaunch={handleAdvancedLaunch}
        launcher={launcher}
        profile={selectedProfile}
        machine={machine}
        isLaunching={isLaunching}
      />

      {/* Environment Preview Modal */}
      <EnvPreviewModal
        isOpen={showEnvPreview}
        onClose={() => setShowEnvPreview(false)}
        profileId={selectedProfile?.profile_id}
        profileName={selectedProfile?.name}
        spaceId={spaceId}
      />
    </div>
  );
};

export default LauncherCard;
